import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getClient } from 'app/api/clientApi';
import { getClientDataForm } from 'app/component/client-widget';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { formatDateFromInstant } from 'app/shared/util/date-utils';
import { Button } from 'primereact/button';
import { SortOrder } from 'primereact/api';
import { TabPanel, TabView } from 'primereact/tabview';
import { useAppSelector } from 'app/config/store';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { AUTHORITIES } from 'app/config/constants'; // Make sure PrimeFlex is imported

const CreateClient: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const account = useAppSelector(state => state.authentication.account);
  const isAdminOrAssistant = hasAnyAuthority(account.authorities, [AUTHORITIES.ADMIN, AUTHORITIES.ASSISTANT]);

  const readOnly = !isAdminOrAssistant || searchParams.has('info');

  useEffect(() => {
    if (id) {
      getClient(id)
        .then(response => {
          if (response.data) {
            setClient(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Handle form submission
  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = axios.put(`/api/clients/${id}`, values);
    } else {
      call = axios.post('/api/clients', values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Klient úspešne uložený.');
        setClient(response.data);

        if (!id) {
          navigate(`/clients?p=${tempParameters}`);
        }
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  const onCancel = () => {
    navigate(`/clients?p=${tempParameters}`);
  };

  const onDetail = rowData => {
    navigate(`/create-contract/${rowData.id}`);
  };

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <Button
          style={{ backgroundColor: '#e31818' }}
          icon="pi pi-info-circle"
          type="button"
          rounded
          outlined
          severity="info"
          onClick={() => onDetail(rowData)}
        />
      </React.Fragment>
    );
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť klientské dáta' : 'Vytvoriť nového klienta'}</h2>
          <TabView>
            <TabPanel header="Základné informácie">
              {getClientDataForm(client, handleSubmit, isSaving, onCancel, isAdminOrAssistant, readOnly)}
            </TabPanel>
            <TabPanel header="Zmluvy klienta">
              <DataTable
                stripedRows
                sortField="id"
                sortOrder={SortOrder.DESC}
                emptyMessage="Tento klient nemá evidované žiadne zmluvy"
                value={client?.contracts}
                responsiveLayout="scroll"
                size="small"
              >
                <Column field={'contractNr'} header="Č. zmluvy" />
                <Column field={'proposalNr'} header="Č. návrhu" />
                <Column field={'product.insuranceCompany.name'} header="Spoločnosť" />
                <Column field={'product.name'} header="Názov produktu" />
                <Column field={'state.name'} header="Stav" />
                <Column field={'payment'} header="Platba" body={v => v.payment + ' EUR'} />
                <Column field={'createdDate'} header="Vytvorené" body={v => formatDateFromInstant(v.createdDate)} />
                <Column header="Akcie" body={actionBodyTemplate}></Column>
              </DataTable>
            </TabPanel>
          </TabView>
        </>
      )}
    </div>
  );
};

export default CreateClient;
